<script setup>

    import { ref, watch, onBeforeUnmount, computed } from 'vue'
    import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { MagnifyingGlassIcon, XCircleIcon as XCircleIconOutline } from '@heroicons/vue/24/outline'
    import { ArrowRightCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
    import { debounce } from 'lodash'
    import axios from 'axios'
    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
    import LwxLotus from '@/Components/LwxLotus.vue'
    import { XMarkIcon } from '@heroicons/vue/20/solid'
    import { useRecentProfilesStore } from '@/Stores'

    const props = defineProps({
        modelValue: {
            type: Boolean,
            default: false
        },
        scope: {
            type: [String, Array],
            required: false,
            validator: (value) => {
                const allowed = ['client', 'prospect', 'caregiver', 'staff', 'lead', 'coi', 'all']
                if (Array.isArray(value)) {
                    return value.every(v => allowed.includes(v))
                }
                return allowed.includes(value)
            },
            default: 'all'
        }
    })

    const emit = defineEmits(['update:modelValue', 'select'])

    const searchQuery = ref('')
    const searchResults = ref([])
    const isLoading = ref(false)
    const hasError = ref(false)
    const searchInput = ref(null)

    const showProspects = ref(false)
    const showClients = ref(false)
    const showLeads = ref(false)
    const showCaregivers = ref(false)
    const showApplicants = ref(false)
    const showCois = ref(false)
    const showMineOnly = ref(false)

    const currentPage = ref(1)
    const isLoadingMore = ref(false)
    const hasMoreResults = ref(true)
    const ITEMS_PER_PAGE = 20

    let abortController = null

    const recentProfilesStore = useRecentProfilesStore()

    const RECENT_PROFILES_DISPLAY_LIMIT = 20

    const historyResults = computed(() => {
        return recentProfilesStore.getByScope(props.scope).slice(0, RECENT_PROFILES_DISPLAY_LIMIT)
    })

    const normalizedScope = computed(() => {
        return Array.isArray(props.scope) ? props.scope : [props.scope]
    })

    const activeFilters = computed(() => {
        const filters = []
        if (showProspects.value) filters.push('prospect')
        if (showClients.value) filters.push('client')
        if (showLeads.value) filters.push('lead')
        if (showCaregivers.value) filters.push('candidate')
        if (showApplicants.value) filters.push('applicant')
        if (showCois.value) filters.push('coi')
        return filters
    })

    const close = () => {
        emit('update:modelValue', false)
        searchQuery.value = ''
        searchResults.value = []
        currentPage.value = 1
        hasMoreResults.value = true
    }

    const clearSearch = () => {
        searchQuery.value = ''
    }

    const performSearch = async (query, shouldAppend = false) => {
        if (!query || query.length < 2) {
            searchResults.value = []
            return
        }

        if (abortController) {
            abortController.abort()
        }
        abortController = new AbortController()
        const currentController = abortController

        const loadingState = shouldAppend ? isLoadingMore : isLoading
        loadingState.value = true
        hasError.value = false

        try {
            const response = await axios.get('/api/v1/quick-search', {
                params: {
                    q: query,
                    scope: props.scope,
                    types: activeFilters.value,
                    mine_only: showMineOnly.value,
                    limit: ITEMS_PER_PAGE,
                    offset: shouldAppend ? (currentPage.value - 1) * ITEMS_PER_PAGE : 0
                },
                signal: currentController.signal
            })

            if (shouldAppend) {
                searchResults.value = [...searchResults.value, ...response.data.data]
            } else {
                searchResults.value = response.data.data
                currentPage.value = 1
            }

            hasMoreResults.value = response.data.data.length === ITEMS_PER_PAGE
        } catch (error) {
            if (axios.isCancel(error)) return
            console.error('Search failed:', error)
            hasError.value = true
            if (!shouldAppend) {
                searchResults.value = []
            }
        } finally {
            if (currentController === abortController) {
                loadingState.value = false
            }
        }
    }

    const selectResult = (result) => {
        recentProfilesStore.addFromQuickSearch(result)
        emit('select', result)
        close()
    }

    const debouncedSearch = debounce(performSearch, 300)

    watch(searchQuery, (newQuery) => {
        hasMoreResults.value = true
        currentPage.value = 1
        debouncedSearch(newQuery)
    })

    watch([showProspects, showClients, showLeads, showCaregivers, showApplicants, showCois, showMineOnly], () => {
        if (searchQuery.value?.length >= 2) {
            hasMoreResults.value = true
            currentPage.value = 1
            debouncedSearch.cancel()
            performSearch(searchQuery.value)
        }
    }, { deep: true })

    const refocusInput = () => {
        setTimeout(() => {
            searchInput.value?.focus()
        }, 0)
    }

    watch(() => props.modelValue, (isOpen) => {
        if (isOpen) {
            setTimeout(() => {
                searchInput.value?.focus()
            }, 100)
        }
    })

    const showClientToggles = computed(() => {
        const scope = normalizedScope.value
        return scope.includes('all') || scope.includes('prospect') || scope.includes('client') || scope.includes('lead')
    })

    const showCaregiverToggles = computed(() => {
        const scope = normalizedScope.value
        return scope.includes('all') || scope.includes('caregiver')
    })

    const showCoiToggle = computed(() => {
        const scope = normalizedScope.value
        return scope.includes('all') || scope.includes('coi')
    })

    // Initialize toggles based on scope
    const initializeToggles = () => {
        const scope = normalizedScope.value
        showProspects.value = scope.includes('all') || scope.includes('prospect')
        showClients.value = scope.includes('all') || scope.includes('client')
        showLeads.value = scope.includes('all') || scope.includes('lead')
        showCaregivers.value = scope.includes('all') || scope.includes('caregiver')
        showApplicants.value = scope.includes('all') || scope.includes('caregiver')
        showCois.value = scope.includes('all') || scope.includes('coi')
    }

    initializeToggles()

    const handleScroll = async (e) => {
        const element = e.target
        if (
            !isLoadingMore.value &&
            hasMoreResults.value &&
            element.scrollHeight - element.scrollTop <= element.clientHeight + 100
        ) {
            currentPage.value++
            await performSearch(searchQuery.value, true)
        }
    }

    onBeforeUnmount(() => {
        debouncedSearch.cancel()
        if (abortController) {
            abortController.abort()
        }
     })

</script>

<template>
    <TransitionRoot
        appear
        :show="modelValue"
        as="template"
    >
        <Dialog
            as="div"
            class="relative z-fullscreen-takeover"
            @close="close"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" />
            </TransitionChild>

            <div class="fixed inset-0 z-fullscreen-takeover w-screen overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="relative w-full mt-16"
                            :class="{
                                'max-w-3xl': !normalizedScope.includes('all'),
                                'max-w-4xl': normalizedScope.includes('all')
                            }"
                        >
                            <div class="flex justify-end mb-2 -mr-8">
                                <button
                                    type="button"
                                    class="rounded-full text-stone-600 hover:text-stone-400 focus:outline-none transition-colors"
                                    @click="close"
                                >
                                    <XMarkIcon class="h-8 w-8" />
                                </button>
                            </div>
                            <DialogPanel
                                class="transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all"
                                @mousedown.self="refocusInput"
                                @click.capture="refocusInput"
                            >
                                <!-- Search Bar -->
                                <div
                                    class="flex p-3 rounded-t-lg focus-within:ring-blue-500 focus-within:ring-1 focus-within:ring-inset border-b border-gray-300">
                                    <MagnifyingGlassIcon class="flex-none inline-flex h-6 w-6 text-gray-400" />
                                    <input
                                        ref="searchInput"
                                        type="text"
                                        v-model="searchQuery"
                                        class="border-0 h-6 flex-grow w-full px-2.5 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:ring-0 bg-transparent"
                                        placeholder="Start typing to search..."
                                        autocomplete="off"
                                        @keydown.escape="close"
                                    />
                                    <button
                                        v-if="searchQuery"
                                        type="button"
                                        class="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-0"
                                        @click="clearSearch"
                                    >
                                        <XCircleIconOutline class="flex-none inline-flex h-6 w-6 -mt-1.5 -mb-1" />
                                    </button>
                                </div>

                                <!-- Filter Toggles -->
                                <div class="px-6 py-4 border-b border-gray-200 bg-lifeworx-blue-100/10">
                                    <div class="flex justify-between">
                                        <!-- Left side filters -->
                                        <div class="flex flex-wrap gap-4">
                                            <template v-if="showClientToggles">
                                                <div class="flex items-center">
                                                    <LwxSwitch
                                                        v-model="showLeads"
                                                        id="leads"
                                                        palette="green"
                                                        :class="[
                                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-lifeworx-green-600 focus:ring-offset-2'
                                                        ]"
                                                    >
                                                        <span :class="[
                                                            showLeads ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        ]" />
                                                    </LwxSwitch>
                                                    <label
                                                        class="ml-1 text-sm font-semibold cursor-pointer"
                                                        for="leads"
                                                    >Leads</label>
                                                </div>

                                                <div class="flex items-center">
                                                    <LwxSwitch
                                                        v-model="showProspects"
                                                        id="prospects"
                                                        palette="green"
                                                        :class="[
                                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-lifeworx-green-600 focus:ring-offset-2'
                                                        ]"
                                                    >
                                                        <span :class="[
                                                            showProspects ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        ]" />
                                                    </LwxSwitch>
                                                    <label
                                                        class="ml-1 text-sm font-semibold cursor-pointer"
                                                        for="prospects"
                                                    >Prospects</label>
                                                </div>

                                                <div class="flex items-center">
                                                    <LwxSwitch
                                                        v-model="showClients"
                                                        id="clients"
                                                        palette="green"
                                                        :class="[
                                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-lifeworx-green-600 focus:ring-offset-2'
                                                        ]"
                                                    >
                                                        <span :class="[
                                                            showClients ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        ]" />
                                                    </LwxSwitch>
                                                    <label
                                                        class="ml-1 text-sm font-semibold cursor-pointer"
                                                        for="clients"
                                                    >Clients</label>
                                                </div>
                                            </template>

                                            <template v-if="showCaregiverToggles">
                                                <div class="flex items-center">
                                                    <LwxSwitch
                                                        v-model="showCaregivers"
                                                        id="caregivers"
                                                        palette="blue"
                                                        :class="[
                                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                                                        ]"
                                                    >
                                                        <span :class="[
                                                            showCaregivers ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        ]" />
                                                    </LwxSwitch>
                                                    <label
                                                        class="ml-1 text-sm font-semibold cursor-pointer"
                                                        for="caregivers"
                                                    >Caregivers</label>
                                                </div>

                                                <div class="flex items-center">
                                                    <LwxSwitch
                                                        v-model="showApplicants"
                                                        id="applicants"
                                                        palette="blue"
                                                        :class="[
                                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                                                        ]"
                                                    >
                                                        <span :class="[
                                                            showApplicants ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        ]" />
                                                    </LwxSwitch>
                                                    <label
                                                        class="ml-1 text-sm font-semibold cursor-pointer"
                                                        for="applicants"
                                                    >Applicants</label>
                                                </div>
                                            </template>

                                            <div
                                                v-if="showCoiToggle"
                                                class="flex items-center"
                                            >
                                                <LwxSwitch
                                                    v-model="showCois"
                                                    id="cois"
                                                    palette="blue"
                                                    :class="[
                                                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                                                    ]"
                                                >
                                                    <span :class="[
                                                        showCois ? 'translate-x-5' : 'translate-x-0',
                                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                    ]" />
                                                </LwxSwitch>
                                                <label
                                                    class="ml-1 text-sm font-semibold cursor-pointer"
                                                    for="cois"
                                                >COIs</label>
                                            </div>
                                        </div>

                                        <!-- Right side Mine Only toggle -->
                                        <div class="flex items-center">
                                            <LwxSwitch
                                                v-model="showMineOnly"
                                                palette="blue"
                                                :class="[
                                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                                                ]"
                                            >
                                                <span :class="[
                                                    showMineOnly ? 'translate-x-5' : 'translate-x-0',
                                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                ]" />
                                            </LwxSwitch>
                                            <span class="ml-2 text-sm font-medium text-gray-900">Mine Only</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Results -->
                                <div class="flex-1 min-h-0 flex flex-col">
                                    <!-- Table Header -->
                                    <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
                                        <div class="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div class="col-span-2">Type</div>
                                            <div class="col-span-3">Name</div>
                                            <div class="col-span-3">Location</div>
                                            <div class="col-span-3">Staff</div>
                                            <div class="col-span-1"></div>
                                        </div>
                                    </div>

                                    <!-- Scrollable body -->
                                    <div
                                        class="h-96 overflow-y-auto"
                                        @scroll="handleScroll"
                                    >
                                        <!-- Loading skeleton -->
                                        <template v-if="isLoading">
                                            <div v-for="n in 6" :key="n" class="px-6 py-4 border-b border-gray-100" :class="n % 2 === 0 ? 'bg-stone-100' : ''">
                                                <div class="grid grid-cols-12 gap-4 items-center">
                                                    <div class="col-span-2"><Skeleton width="60%" height="16px" /></div>
                                                    <div class="col-span-3"><Skeleton width="80%" height="16px" /></div>
                                                    <div class="col-span-3"><Skeleton width="70%" height="16px" /></div>
                                                    <div class="col-span-3"><Skeleton width="75%" height="16px" /></div>
                                                    <div class="col-span-1 flex justify-end"><Skeleton shape="circle" width="32px" height="32px" /></div>
                                                </div>
                                            </div>
                                        </template>

                                        <!-- Error -->
                                        <div
                                            v-else-if="hasError"
                                            class="flex flex-col items-center justify-center py-16 text-red-600"
                                        >
                                            <ExclamationTriangleIcon class="h-8 w-8 mb-2" />
                                            <p class="text-sm">An error occurred while searching. Please try again.</p>
                                        </div>

                                        <template v-else>
                                            <!-- Search results -->
                                            <template v-if="searchQuery && searchQuery.length >= 2">
                                                <div
                                                    v-if="searchResults.length === 0 && !isLoadingMore"
                                                    class="flex flex-col items-center justify-center py-8 text-gray-500"
                                                >
                                                    <MagnifyingGlassIcon class="h-6 w-6 mb-1" />
                                                    <p class="text-sm">No results found.</p>
                                                </div>
                                                <div
                                                    v-for="result in searchResults"
                                                    :key="result.id"
                                                    class="px-6 py-4 border-b border-gray-100 hover:bg-stone-200 even:bg-stone-100 cursor-pointer"
                                                    @click="selectResult(result)"
                                                >
                                                    <div class="grid grid-cols-12 gap-4 items-center">
                                                        <!-- Type -->
                                                        <div class="col-span-2">
                                                            <span class="text-sm font-semibold text-stone-400 capitalize">
                                                                {{ result.type.join(', ') }}
                                                            </span>
                                                        </div>
                                                        <!-- Name -->
                                                        <div class="col-span-3">
                                                            <div class="text-md font-semibold text-lifeworx-blue-800">{{ result.name }}</div>
                                                            <div v-if="result.subtitle" class="text-sm text-gray-500">{{ result.subtitle }}</div>
                                                        </div>
                                                        <!-- Location -->
                                                        <div class="col-span-3">
                                                            <div v-if="result.location" class="text-sm font-semibold text-stone-500">{{ result.location }}</div>
                                                            <div v-else class="text-sm">
                                                                <ExclamationTriangleIcon class="h-5 w-5 text-lifeworx-red-600" />
                                                            </div>
                                                        </div>
                                                        <!-- Staff -->
                                                        <div class="col-span-3">
                                                            <div v-if="result.staff" class="flex items-center text-sm">
                                                                <LwxLotus class="inline-flex h-3.5 bg-white" />
                                                                <span class="font-semibold text-lifeworx-blue-800 ml-1 whitespace-nowrap truncate">{{ result.staff }}</span>
                                                            </div>
                                                        </div>
                                                        <!-- Action Button -->
                                                        <div class="col-span-1 flex justify-end">
                                                            <div v-if="result.has_alert || result.hard_no_status" class="mr-2">
                                                                <ExclamationTriangleIcon class="h-5 w-5 text-red-500" />
                                                            </div>
                                                            <button class="rounded-full" @click="selectResult(result)">
                                                                <ArrowRightCircleIcon class="h-8 w-8 text-lifeworx-green-700/70 hover:text-lifeworx-green-700 transition-colors" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Loading more indicator -->
                                                <div v-if="isLoadingMore" class="px-6 py-4 border-b border-gray-100">
                                                    <div class="grid grid-cols-12 gap-4 items-center">
                                                        <div class="col-span-12 flex justify-center">
                                                            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-lifeworx-blue-600"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>

                                            <!-- Recent Profiles section -->
                                            <template v-if="historyResults.length > 0 && searchResults.length < 6">
                                                <div
                                                    v-for="result in historyResults"
                                                    :key="'h-' + (result.key || result.id)"
                                                    class="px-6 py-4 border-b border-gray-100 hover:bg-stone-200 even:bg-stone-100 cursor-pointer"
                                                    @click="selectResult(result)"
                                                >
                                                    <div class="grid grid-cols-12 gap-4 items-center">
                                                        <!-- Type -->
                                                        <div class="col-span-2 flex flex-col gap-1">
                                                            <span class="text-2xs font-bold text-lifeworx-blue-700/50 uppercase tracking-wide">RECENT</span>
                                                            <span class="text-sm font-semibold text-stone-400 capitalize">
                                                                {{ result.type.join(', ') }}
                                                            </span>
                                                        </div>
                                                        <!-- Name -->
                                                        <div class="col-span-3">
                                                            <div class="text-md font-semibold text-lifeworx-blue-800">{{ result.name }}</div>
                                                            <div v-if="result.subtitle" class="text-sm text-gray-500">{{ result.subtitle }}</div>
                                                        </div>
                                                        <!-- Location -->
                                                        <div class="col-span-3">
                                                            <div v-if="result.location" class="text-sm font-semibold text-stone-500">{{ result.location }}</div>
                                                            <div v-else class="text-sm">
                                                                <ExclamationTriangleIcon class="h-5 w-5 text-lifeworx-red-600" />
                                                            </div>
                                                        </div>
                                                        <!-- Staff -->
                                                        <div class="col-span-3">
                                                            <div v-if="result.staff" class="flex items-center text-sm">
                                                                <LwxLotus class="inline-flex h-3.5 bg-white" />
                                                                <span class="font-semibold text-lifeworx-blue-800 ml-1 whitespace-nowrap truncate">{{ result.staff }}</span>
                                                            </div>
                                                        </div>
                                                        <!-- Action Button -->
                                                        <div class="col-span-1 flex justify-end">
                                                            <div v-if="result.has_alert || result.hard_no_status" class="mr-2">
                                                                <ExclamationTriangleIcon class="h-5 w-5 text-red-500" />
                                                            </div>
                                                            <button class="rounded-full" @click="selectResult(result)">
                                                                <ArrowRightCircleIcon class="h-8 w-8 text-lifeworx-green-700/70 hover:text-lifeworx-green-700 transition-colors" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="px-6 py-3 text-center">
                                                    <button
                                                        type="button"
                                                        class="cursor-pointer text-xs font-semibold text-lifeworx-red-800/50 hover:text-lifeworx-red-600 transition-colors uppercase"
                                                        @click="recentProfilesStore.clear()"
                                                    >
                                                        Clear Recent Profiles
                                                    </button>
                                                </div>
                                            </template>

                                            <!-- Empty state -->
                                            <div
                                                v-if="(historyResults.length === 0 || searchResults.length >= 6) && (!searchQuery || searchResults.length === 0)"
                                                class="flex flex-col items-center justify-center py-16 text-gray-500"
                                            >
                                                <MagnifyingGlassIcon class="h-8 w-8 mb-2" />
                                                <p v-if="searchQuery && searchQuery.length > 0 && searchQuery.length < 2" class="text-sm">Please enter at least 2 characters to search.</p>
                                                <p v-else-if="!searchQuery" class="text-sm">Start typing to search for people...</p>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
